import { useEffect, useRef } from "react";
import type { SlashCommand } from "../slashCommands";
import styles from "./SlashCommandPalette.module.css";

interface Props {
  readonly commands: readonly SlashCommand[];
  readonly activeIndex: number;
  readonly onSelect: (cmd: SlashCommand) => void;
}

export function SlashCommandPalette({ commands, activeIndex, onSelect }: Readonly<Props>) {
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (commands.length === 0) {
    return (
      <div className={styles.palette}>
        <div className={styles.empty}>No matching commands</div>
      </div>
    );
  }

  return (
    <div className={styles.palette} role="menu" aria-label="Slash commands">
      {commands.map((cmd, idx) => {
        const isActive = idx === activeIndex;
        return (
          <button
            key={cmd.slash}
            ref={isActive ? activeRef : undefined}
            type="button"
            role="menuitem"
            aria-current={isActive || undefined}
            className={`${styles.item} ${isActive ? styles.itemActive : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(cmd);
            }}
          >
            <span className={styles.command}>{cmd.slash}</span>
            <span className={styles.desc}>{cmd.description}</span>
          </button>
        );
      })}
    </div>
  );
}
